import { useCallback, useMemo, useState } from "react"
import { ArrowUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Table/Table"
import { Badge } from "../Badge/Badge"
import {
  TradesPagination,
  usePaginationState,
} from "../TradesPagination/TradesPagination"
import type { Trade } from "../types/backtest"

export type TradesSortKey =
  | "openDate"
  | "closeDate"
  | "dir"
  | "vol"
  | "entry"
  | "exitP"
  | "sl"
  | "tp"
  | "pips"
  | "pnl"

export type SortDirection = "asc" | "desc"

export interface TradeSubTab {
  id: string
  label: string
  emptyTitle?: string
  emptySubtitle?: string
}

export interface TradesTableProps {
  trades: Trade[]
  subTabs?: TradeSubTab[]
  activeSubTabId?: string
  defaultSubTabId?: string
  onSubTabChange?: (id: string) => void
  sortKey?: TradesSortKey
  defaultSortKey?: TradesSortKey
  sortDirection?: SortDirection
  defaultSortDirection?: SortDirection
  onSortChange?: (key: TradesSortKey, direction: SortDirection) => void
  onRowClick?: (trade: Trade) => void
  pageSize?: number
  defaultPageSize?: number
  initialBalance?: number
  emptyMessage?: string
  /** Hide the sub-tab toolbar (Trades / Open Trades / Pending Orders). Defaults to false. */
  hideSubTabs?: boolean
  /** Hide the Pips column (header + body cell). Defaults to false. */
  hidePips?: boolean
  /** Controlled current page (0-indexed). When set, the table no longer slices `trades` and routes page changes to `onPageChange`. */
  page?: number
  /** Total row count across all pages (server-side pagination). Falls back to `trades.length` when not set. */
  totalRows?: number
  /** Page-change handler. Required when `page` is controlled. */
  onPageChange?: (page: number) => void
  /** Page-size-change handler. When `pageSize` is controlled, the table calls this instead of its internal setter. */
  onPageSizeChange?: (size: number) => void
  className?: string
}

const DEFAULT_SUBTABS: TradeSubTab[] = [
  { id: "history", label: "Trades" },
  {
    id: "open",
    label: "Open Trades",
    emptyTitle: "No open trades",
    emptySubtitle: "Active positions will appear here during live trading.",
  },
  {
    id: "pending",
    label: "Pending Orders",
    emptyTitle: "No pending orders",
    emptySubtitle: "Limit and stop orders will appear here when placed.",
  },
]

const HEAD_BASE = "text-caption text-muted-foreground uppercase tracking-wider"
const NUM_CELL = "text-caption text-muted-foreground w-20 text-right hidden 2xl:table-cell"

const ACCESSORS: Record<TradesSortKey, (t: Trade) => number | string> = {
  openDate: (t) => Date.parse(t.openDate),
  closeDate: (t) => Date.parse(t.closeDate),
  dir: (t) => t.dir,
  vol: (t) => parseFloat(String(t.vol)),
  entry: (t) => parseFloat(String(t.entry)),
  exitP: (t) => parseFloat(String(t.exitP)),
  sl: (t) => (t.sl ? parseFloat(String(t.sl)) : 0),
  tp: (t) => (t.tp ? parseFloat(String(t.tp)) : 0),
  pips: (t) => t.pips ?? 0,
  pnl: (t) => t.pnl,
}

export function TradesTable({
  trades,
  subTabs = DEFAULT_SUBTABS,
  activeSubTabId: controlledSubTab,
  defaultSubTabId = "history",
  onSubTabChange,
  sortKey: controlledKey,
  defaultSortKey = "openDate",
  sortDirection: controlledDir,
  defaultSortDirection = "desc",
  onSortChange,
  onRowClick,
  pageSize: controlledPageSize,
  defaultPageSize = 15,
  emptyMessage = "No trades in this period",
  hideSubTabs = false,
  hidePips = false,
  page: controlledPage,
  totalRows: controlledTotalRows,
  onPageChange,
  onPageSizeChange,
  className,
}: TradesTableProps) {
  const [internalSubTab, setInternalSubTab] = useState<string>(defaultSubTabId)
  const subTab = controlledSubTab ?? internalSubTab
  const handleSubTab = useCallback(
    (id: string) => {
      setInternalSubTab(id)
      onSubTabChange?.(id)
    },
    [onSubTabChange],
  )

  const [internalSortKey, setInternalSortKey] =
    useState<TradesSortKey>(defaultSortKey)
  const [internalSortDir, setInternalSortDir] =
    useState<SortDirection>(defaultSortDirection)
  const sortKey = controlledKey ?? internalSortKey
  const sortDir = controlledDir ?? internalSortDir

  const toggleSort = useCallback(
    (key: TradesSortKey) => {
      let nextDir: SortDirection
      if (sortKey === key) {
        nextDir = sortDir === "asc" ? "desc" : "asc"
        setInternalSortDir(nextDir)
      } else {
        nextDir = "desc"
        setInternalSortKey(key)
        setInternalSortDir(nextDir)
      }
      onSortChange?.(key, nextDir)
    },
    [sortKey, sortDir, onSortChange],
  )

  const pagination = usePaginationState(controlledPageSize ?? defaultPageSize)

  const isPageControlled = controlledPage !== undefined
  const currentPage = isPageControlled ? controlledPage : pagination.page
  const setCurrentPage = useCallback(
    (next: number) => {
      if (isPageControlled) {
        onPageChange?.(next)
      } else {
        pagination.setPage(next)
        onPageChange?.(next)
      }
    },
    [isPageControlled, onPageChange, pagination],
  )

  const isPageSizeControlled = controlledPageSize !== undefined
  const effectivePageSize = isPageSizeControlled
    ? controlledPageSize
    : pagination.pageSize
  const setEffectivePageSize = useCallback(
    (next: number) => {
      if (isPageSizeControlled) {
        onPageSizeChange?.(next)
      } else {
        pagination.setPageSize(next)
        onPageSizeChange?.(next)
      }
    },
    [isPageSizeControlled, onPageSizeChange, pagination],
  )

  const sortedTrades = useMemo(() => {
    const accessor = ACCESSORS[sortKey] ?? ACCESSORS.openDate
    return [...trades].sort((a, b) => {
      const av = accessor(a)
      const bv = accessor(b)
      const cmp =
        typeof av === "string" && typeof bv === "string"
          ? av.localeCompare(bv)
          : (av as number) - (bv as number)
      return sortDir === "asc" ? cmp : -cmp
    })
  }, [trades, sortKey, sortDir])

  const totalRows = controlledTotalRows ?? sortedTrades.length
  const pageCount = Math.max(1, Math.ceil(totalRows / effectivePageSize))
  const pagedTrades = isPageControlled
    ? sortedTrades
    : sortedTrades.slice(
        pagination.page * effectivePageSize,
        (pagination.page + 1) * effectivePageSize,
      )

  const activeSubTab = subTabs.find((t) => t.id === subTab) ?? subTabs[0]

  function SortHeader({
    label,
    keyId,
  }: {
    label: string
    keyId: TradesSortKey
  }) {
    const active = sortKey === keyId
    return (
      <button
        type="button"
        onClick={() => toggleSort(keyId)}
        className={cn(
          "inline-flex items-center gap-1 cursor-pointer transition-colors",
          active ? "text-foreground" : "hover:text-foreground",
        )}
      >
        {label}
        <ArrowUpDown
          size={9}
          strokeWidth={1.5}
          className={active ? "text-foreground" : "text-muted-foreground/50"}
        />
      </button>
    )
  }

  return (
    <div className={cn("flex flex-col h-full min-h-0", className)}>
      {!hideSubTabs && (
        <div className="flex items-center gap-4 px-4 border-b border-border shrink-0 h-11">
          {subTabs.map((t) => {
            const active = activeSubTab?.id === t.id
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleSubTab(t.id)}
                className={cn(
                  "text-caption pb-1 cursor-pointer transition-colors border-b-[1.5px]",
                  active
                    ? "font-medium text-foreground border-foreground"
                    : "font-normal text-muted-foreground border-transparent hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            )
          })}
        </div>
      )}

      {!hideSubTabs && activeSubTab && activeSubTab.id !== "history" && activeSubTab.emptyTitle && (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-12">
          <p className="text-body text-muted-foreground">{activeSubTab.emptyTitle}</p>
          {activeSubTab.emptySubtitle && (
            <p className="text-caption text-muted-foreground/60 mt-1">
              {activeSubTab.emptySubtitle}
            </p>
          )}
        </div>
      )}

      {(hideSubTabs || activeSubTab?.id === "history") &&
        (sortedTrades.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-caption text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-4">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className={cn(HEAD_BASE, "w-32 text-left hidden 2xl:table-cell")}>
                    <SortHeader label="Open Time" keyId="openDate" />
                  </TableHead>
                  <TableHead className={cn(HEAD_BASE, "w-32 text-left")}>
                    <SortHeader label="Close Time" keyId="closeDate" />
                  </TableHead>
                  <TableHead className={cn(HEAD_BASE, "w-16 text-center")}>
                    <SortHeader label="Type" keyId="dir" />
                  </TableHead>
                  <TableHead className={cn(HEAD_BASE, "w-16 text-right hidden 2xl:table-cell")}>
                    <SortHeader label="Lots" keyId="vol" />
                  </TableHead>
                  <TableHead className={cn(HEAD_BASE, "w-20 text-right hidden 2xl:table-cell")}>
                    <SortHeader label="Entry" keyId="entry" />
                  </TableHead>
                  <TableHead className={cn(HEAD_BASE, "w-20 text-right hidden 2xl:table-cell")}>
                    <SortHeader label="Exit" keyId="exitP" />
                  </TableHead>
                  <TableHead className={cn(HEAD_BASE, "w-20 text-right hidden 2xl:table-cell")}>
                    <SortHeader label="SL" keyId="sl" />
                  </TableHead>
                  <TableHead className={cn(HEAD_BASE, "w-20 text-right hidden 2xl:table-cell")}>
                    <SortHeader label="TP" keyId="tp" />
                  </TableHead>
                  {!hidePips && (
                    <TableHead className={cn(HEAD_BASE, "w-20 text-right")}>
                      <SortHeader label="Pips" keyId="pips" />
                    </TableHead>
                  )}
                  <TableHead className={cn(HEAD_BASE, "w-24 text-right")}>
                    <SortHeader label="$ Gain" keyId="pnl" />
                  </TableHead>
                  <TableHead className={cn(HEAD_BASE, "w-20 text-right hidden 2xl:table-cell")}>
                    <SortHeader label="% Gain" keyId="pnl" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedTrades.map((t, i) => {
                  const globalIdx = currentPage * effectivePageSize + i
                  const pnlPositive = t.pnl >= 0
                  return (
                    <TableRow
                      key={`${t.openDate}-${globalIdx}`}
                      className={cn(
                        onRowClick && "hover:bg-muted/50 cursor-pointer",
                      )}
                      onClick={() => onRowClick?.(t)}
                    >
                      <TableCell className="text-caption text-muted-foreground w-32 hidden 2xl:table-cell">
                        {t.openDate}
                      </TableCell>
                      <TableCell className="text-caption text-foreground w-32">
                        {t.closeDate}
                      </TableCell>
                      <TableCell className="w-16 text-center">
                        <Badge
                          variant="outline"
                          className={cn(
                            "font-semibold",
                            t.dir === "BUY" ? "text-success" : "text-danger",
                          )}
                        >
                          {t.dir}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-caption text-muted-foreground w-16 text-right hidden 2xl:table-cell">
                        {t.vol}
                      </TableCell>
                      <TableCell className={NUM_CELL}>{t.entry}</TableCell>
                      <TableCell className={NUM_CELL}>{t.exitP}</TableCell>
                      <TableCell className={NUM_CELL}>{t.sl ?? "—"}</TableCell>
                      <TableCell className={NUM_CELL}>{t.tp ?? "—"}</TableCell>
                      {!hidePips && (
                        <TableCell
                          className={cn(
                            "text-caption w-20 text-right",
                            pnlPositive ? "text-success" : "text-danger",
                          )}
                        >
                          {pnlPositive ? "+" : ""}
                          {t.pips?.toFixed(1) ?? "—"}
                        </TableCell>
                      )}
                      <TableCell
                        className={cn(
                          "text-caption w-24 text-right",
                          pnlPositive ? "text-success" : "text-danger",
                        )}
                      >
                        {pnlPositive ? "+" : ""}
                        {t.pnl.toFixed(2)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-caption w-20 text-right hidden 2xl:table-cell",
                          pnlPositive ? "text-success" : "text-danger",
                        )}
                      >
                        {t.gain ?? "—"}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        ))}

      {(hideSubTabs || activeSubTab?.id === "history") && totalRows > 0 && (
        <div className="shrink-0">
          <TradesPagination
            page={currentPage}
            pageSize={effectivePageSize}
            pageCount={pageCount}
            totalRows={totalRows}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setEffectivePageSize(size)
              setCurrentPage(0)
            }}
          />
        </div>
      )}
    </div>
  )
}
